import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { NotesPopup_OptimizedV2 as NotesPopupOptimizedV2 } from "@/Common/Components/NotesPopup/NotesPopup_OptimizedV2";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Guid } from "@/Common/Models/Guid";
import { CommentActionType } from "@/Common/Models/MedicalRequest/Enum/CommentActionType";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { IndividualMessageNote } from "@/Common/Models/NotesPopup/IndividualMessageNote";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { PortfolioConsultationRequestsSortOrderType } from "@/Common/Models/UI/Enums";
import { ConsultationRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { MyConsultationRequestsTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { IConsultationRequestInboxModel } from "@/Portfolio/Models/UI/IConsultationRequestsInboxModel";
import { IConsultationRequestPopupDataModel } from "@/Portfolio/Models/UI/IConsultationRequestsPopupDataModel";
import { MedicalRequestStatus } from "../Models/MedicalRequestStatus";
import { ConsultationRequestsInboxItem } from "../Models/MyNotesInboxItem";
import { SendFromMyConsultationsStrategy } from "../Services/Portal/Strategies/SendFromMyConsultationsStrategy";
import { MedicalRequestPopupController } from "./MedicalRequestForm/MedicalRequestPopupController";
import { MedicalRequestSettingsContainer } from "./MedicalRequestSettingsContainer";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface IConsultationRequestInboxProps {
    Model: IConsultationRequestInboxModel;
    ConsultationRequestPopupModel: IConsultationRequestPopupDataModel
}

class ConsultationRequestInboxState {
    public selectedConsultationId: string;

    public actionPopupIsOpen: boolean;
    public actionPopupForConsultationRequest: ConsultationRequestsInboxItem;
    public actionPopupPositionY: number;
    public actionPopupPositionX: number;

    public constructor(init?: Partial<ConsultationRequestInboxState>) {
        Object.assign(this, init);
    }

    public static getDefault(): ConsultationRequestInboxState {
        return new ConsultationRequestInboxState({
            selectedConsultationId: "",
            actionPopupIsOpen: false,
            actionPopupPositionX: 0,
            actionPopupPositionY: 0
        });
    }
}

class ConsultationRequestInbox extends BaseReactComponent<IConsultationRequestInboxProps, ConsultationRequestInboxState> {
    private readonly helper = sessionStore.helper;
    private readonly footerClassName = "ws-footer";

    public constructor(props: IConsultationRequestInboxProps) {
        super(props);
        this.state = ConsultationRequestInboxState.getDefault();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.Consultation;

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.defaultTableOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = MyleTableHelper.getDefaultRenderOptions(columnsOptions.toArray());
        const columnConfigModel: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                {this.state.selectedConsultationId && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.ConsultationRequest}
                        id={this.state.selectedConsultationId}
                        showPrintHeader={true}
                        closeAfterButtonClick={true}
                        isDisplayAsReadonly={!cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}
                        onClose={() => this.mutateState(s => s.selectedConsultationId = null)}
                        onPrintSuccess={async exportableFile => PrintHelper.ShowPrintPreviewFileName((await exportableFile.getFetchUrlDtoAsync())?.FetchUrl)}
                        onSignClick={(model, printInfo) => {
                            this.mutateState(
                                s => s.selectedConsultationId = null,
                                () => {
                                    ExportableFileDispatcher.openSignFaxPrintPopupForConsultationRequest({
                                        patientContactId: printInfo.patientId,
                                        consultationRequestId: model.id,
                                        consultationRequestDate: model.requestDate,
                                        exportMode: ExportMode.Sign,
                                        isPortalSyncEnabled: false,
                                        signedPrintAuthorizations: model.signedPrintAuthorizations,
                                        requestingStaffContactFacetId: model.requestingStaffContactFacetId
                                    });
                                }
                            );
                        }}
                        patientContactIdForNewRequests={null}
                        onSaveSuccess={() => sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_SearchBoxTextChanged)}
                        onFaxPrintPopupClose={() => sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_Reload)}
                    />
                )}

                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MyConsultationRequestsLabel")}
                    searchText={this.props.Model.get("GlobalPortfolioSearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalPortfolioSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_SearchBoxTextChanged)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_ShowOptionsButtonClick)}
                />

                <div id="posref" className="ws-scrollable">
                    {this.props.Model.get("ShowMyConsultationRequestsDisplaySettings")
                        && (
                            <MedicalRequestSettingsContainer
                                className="medical-request-settings"
                                footerClassName="medical-setting-footer"
                                type={MedicalRequestType.ConsultationRequest}
                                model={this.props.Model}
                                onCloseOp={LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_CancelClick}
                                onOkOp={LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_OkClick}
                                onColumnOrderingButtonClickOp={LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_ColumnOrderingPopup_Open}
                                onResultsPerPageChangeOp={LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_OnResultsPerPageChange}
                            />
                        )
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="ConsultationRequestsInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickTarget={MyleTableTarget.Myle_ConsultationRequests_Inbox_RowClick}
                            RowClickFunction={data => this.mutateState(s => s.selectedConsultationId = data.Model.ConsultationRequestId)}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioMyConsultationRequests_Search_ColumnSortClick}
                            IsRowLocked={(item: any) => item.Model.IsAccessProhibited}
                            TableDataCy="portfolio-table"
                        />
                    </div>
                </div>
                <div className={`${this.footerClassName} table-pager-footer`}>
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.MyConsultationRequests_Search_CurrentPageChange}
                            />
                        </div>
                    </div>
                </div>
                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigModel}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}

                {this.props.Model.get("IsSendToPatientPortalPopupOpen") && (
                    <div>
                        <SendToPortalPopup
                            model={this.props.Model.get("PortalLinkedItemModel")}
                            strategy={new SendFromMyConsultationsStrategy(this.props.Model.get("PortalLinkedItemModel"))}
                        />
                    </div>
                )}

                {this.state.actionPopupIsOpen && (this.renderActionPopup())}
            </div>
        );
    }

    private labels = {
        Patient: "PatientLabel",
        Specialization: "SpecializationLabel",
        RequestingStaff: "RequestingStaffLabel",
        CreatedBy: "CreatedByLabel",
        DueDate: "DueDateLabel",
        Priority: "PriorityLabel",
        Status: "StatusLabel",
        CreationDate: "CreationDateLabel",
        ConsultationDate: "ConsultationDateLabel",
        Recipient: "RecipientLabel",
        PatientNextAppointmentDateTime: "FutureAPPTLabel",
        SharePortalLabel: "PortalLabel",
        ActionLabel: "ActionLabel"
    };

    private fieldNames = {
        Patient: "Patient",
        Specialization: "Specialization",
        RequestingStaff: "RequestingStaff",
        CreatedBy: "CreatedBy",
        DueDate: "DueDate",
        Priority: "Priority",
        Status: "Status",
        CreationDate: "CreationDate",
        ConsultationDate: "ConsultationDate",
        Recipient: "Recipient",
        PatientNextAppointmentDateTime: "PatientNextAppointmentDateTime",
        SharePortalButton: "SharePortalButton",
        Action: "Action"
    };

    private defaultTableOptions(): MyleTableColumnOptions[] {
        const myleTableColumnOptionsArray = [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Patient),
                FieldName: this.fieldNames.Patient,
                RowClickEnabled: true,
                WidthPercentageDefault: 10,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.Patient,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.Patient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Specialization),
                FieldName: this.fieldNames.Specialization,
                RowClickEnabled: true,
                WidthPercentageDefault: 10,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.Speciality,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.Speciality
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.RequestingStaff),
                FieldName: this.fieldNames.RequestingStaff,
                RowClickEnabled: true,
                WidthPercentageDefault: 10,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.RequestingStaff,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.RequestingStaff
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.CreatedBy),
                FieldName: this.fieldNames.CreatedBy,
                RowClickEnabled: true,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.CreationDate),
                FieldName: this.fieldNames.CreationDate,
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.CreationDate,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.CreationDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.DueDate),
                FieldName: this.fieldNames.DueDate,
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.DueDate,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.DueDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Priority),
                FieldName: this.fieldNames.Priority,
                RowClickEnabled: true,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Status),
                FieldName: this.fieldNames.Status,
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioConsultationRequestsSortOrderType.Status,
                SortDirection: this.props.Model.get("OrderByDirection"),
                IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.Status
            })
        ];

        if (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements) {
            myleTableColumnOptionsArray.push(
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.ConsultationDate),
                    FieldName: this.fieldNames.ConsultationDate,
                    RowClickEnabled: true,
                    WidthPercentageDefault: 8,
                    IsSortable: true,
                    SortColumn: PortfolioConsultationRequestsSortOrderType.ConsultationDate,
                    SortDirection: this.props.Model.get("OrderByDirection"),
                    IsSortActive: this.props.Model.get("OrderByColumn") === PortfolioConsultationRequestsSortOrderType.ConsultationDate
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.Recipient),
                    FieldName: this.fieldNames.Recipient,
                    RowClickEnabled: true,
                    WidthPercentageDefault: 8
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.PatientNextAppointmentDateTime),
                    FieldName: this.fieldNames.PatientNextAppointmentDateTime,
                    RowClickEnabled: true,
                    WidthPercentageDefault: 8
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.SharePortalLabel),
                    FieldName: this.fieldNames.SharePortalButton,
                    RowClickEnabled: false,
                    WidthPercentageDefault: 5
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.ActionLabel),
                    FieldName: this.fieldNames.Action,
                    RowClickEnabled: false,
                    IsFixedInPlace: true,
                    WidthPercentageDefault: 5
                })
            );
        }
        return myleTableColumnOptionsArray;
    }

    private getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MyConsultationRequestsTableItem>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.Patient:
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.IsAccessProhibited
                                        ? <span>{cacheStore.GetString("Allowlist_BlockedLabel")}</span>
                                        : (
                                            <a
                                                className={this.getUrgentClassName(item)}
                                                onClick={e => {
                                                    sessionStore.dispatch(LocalOperations.Portfolio.MyConsultations_OnPatientClick, { item: item });
                                                    e.stopPropagation();
                                                }}
                                            >
                                                {item.Model.PatientName}
                                            </a>
                                        )
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.Specialization:
                        return {
                            renderCell: item => <div className={this.getUrgentClassName(item)}>{item.Model.IsAccessProhibited ? cacheStore.GetString("Allowlist_BlockedLabel") : item.Model.Speciality}</div>
                        };

                    case this.fieldNames.RequestingStaff:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {item.Model.AttendingDoctorsName}
                                </div>
                            )
                        };

                    case this.fieldNames.CreatedBy:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {this.getCreatedByString(item.Model.CreatedByContactId)}
                                </div>
                            )
                        };

                    case this.fieldNames.CreationDate:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {DateHelper.formatDate(item.Model.ConsultationRequestDate, "YYYY/MM/DD")}
                                </div>
                            )
                        };

                    case this.fieldNames.DueDate:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {DateHelper.formatDate(item.Model.DueDate, "YYYY/MM/DD")}
                                </div>
                            )
                        };

                    case this.fieldNames.Priority:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {item.Model.IsAccessProhibited
                                        ? cacheStore.GetString("Allowlist_BlockedLabel")
                                        : (this.getPriorityString(item.Model.PriorityDropdownId))
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.Status:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {this.getStatusString(item.Model.Status)}
                                </div>
                            )
                        };

                    case this.fieldNames.ConsultationDate:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {item.Model.IsAccessProhibited
                                        ? cacheStore.GetString("Allowlist_BlockedLabel")
                                        : this.getFormattedConsultationDateTime(item.Model?.ReferralConsultationDate)
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.Recipient:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {item.Model.IsAccessProhibited
                                        ? cacheStore.GetString("Allowlist_BlockedLabel")
                                        : item.Model.Recipient
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.PatientNextAppointmentDateTime:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {item.Model.IsAccessProhibited
                                        ? cacheStore.GetString("Allowlist_BlockedLabel")
                                        : <span>{this.getNextAppointmentText(item)}</span>
                                    }
                                </div>
                            )
                        };
                    case this.fieldNames.SharePortalButton:
                        return {
                            renderCell: item => (
                                this.canSendToPortal(item.Model) && !item.Model.IsAccessProhibited && (
                                    <PortalStatusButton
                                        onClick={() => this.onPortalClick(item.Model)}
                                        {...PortalHelper.getPortalButtonPropsForShareableItem(item.Model)}
                                    />
                                )
                            )
                        };

                    case this.fieldNames.Action:
                        return {
                            renderCell: item => {
                                const commentCount = (item.Model.Comments?.filter(comment => comment.ActionType == null || comment.ActionType == CommentActionType.UserComment).length ?? 0);

                                const displayValue = commentCount > 0 ? commentCount : "+";
                                const spanCssClass = commentCount > 0 ? "red-text" : "";

                                return (
                                    <button
                                        className="clear-button"
                                        onClick={event => this.showActionPopup(item.Model, event)}
                                    >
                                        [ <span className={spanCssClass}>{displayValue}</span> ]
                                    </button>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    private showActionPopup(item: ConsultationRequestsInboxItem, event: React.MouseEvent): void {
        const actionPopupPositionY = event.clientY;
        const actionPopupPositionX = event.clientX - NotesPopupOptimizedV2.popupWidth;

        this.setState({
            actionPopupIsOpen: true,
            actionPopupForConsultationRequest: item,
            actionPopupPositionX,
            actionPopupPositionY
        });
    }

    private closeActionPopup(): void {
        this.setState({
            actionPopupIsOpen: false,
            actionPopupForConsultationRequest: null,
            actionPopupPositionX: 0,
            actionPopupPositionY: 0
        });
    }

    private canBuildGeneratedBy(consultationRequestsInboxItem: ConsultationRequestsInboxItem): boolean {
        const generationInfo = this.getGenerationInformation(consultationRequestsInboxItem);
        return (!!generationInfo?.employeeId && !!generationInfo?.generatedDate);
    }

    private buildGeneratedComment(consultationRequestsInboxItem: ConsultationRequestsInboxItem): IndividualMessageNote {
        const generationInfo = this.getGenerationInformation(consultationRequestsInboxItem);

        const comment = new IndividualMessageNote();
        comment.DateCreated = generationInfo?.generatedDate.toISOString() ?? "";
        comment.ContactFacetId = generationInfo?.employeeId ?? "";
        comment.Note = cacheStore.GetString("ConsultationRequestAction_RequestGenerated");

        return comment;
    }

    private getGenerationInformation(consultationRequestsInboxItem: ConsultationRequestsInboxItem): { employeeId: Guid, generatedDate: Date } | null {
        if (!consultationRequestsInboxItem)
            return null;

        let employeeId: Guid = consultationRequestsInboxItem.FirstPrintedByEmployeeId;
        let generatedDate: Date = new Date(consultationRequestsInboxItem.FirstPrintedDate);

        if (consultationRequestsInboxItem.IsSignedOff) {
            const signedOffDate = new Date(consultationRequestsInboxItem.SignedOffDate);
            if (!generatedDate || signedOffDate < generatedDate) {
                generatedDate = signedOffDate;
                employeeId = consultationRequestsInboxItem.SignedOffByEmployeeId;
            }
        }

        return { employeeId, generatedDate };
    }

    private renderActionPopup(): JSX.Element {
        if (!this.state.actionPopupIsOpen) return null;

        let notes: IndividualMessageNote [] = this.state.actionPopupForConsultationRequest
            .Comments?.map(p => ({
                Id: p.Id,
                ContactFacetId: p.EmployeeId,
                Note: p.Value,
                DateCreated: p.CreateDate.toLocaleString(),
                ActionResultLookupId: p.ActionResultLookupId,
                ActionType: p.ActionType
            })) ?? [];

        if (notes
            && cacheStore.ClinicSettings.EnableConsultationRequestHistory
            && this.canBuildGeneratedBy(this.state.actionPopupForConsultationRequest)
        ) {
            notes.push(this.buildGeneratedComment(this.state.actionPopupForConsultationRequest));
        }

        notes = notes?.sortByProperty(comment => LuxonDateTimeParser.parse(comment?.DateCreated));

        return (
            <NotesPopupOptimizedV2
                controlId="consultation_requests_comments_dropdown_main"
                itemId={this.state.actionPopupForConsultationRequest.ConsultationRequestId}
                notes={notes}
                leftPosition={this.state.actionPopupPositionX}
                topPosition={this.state.actionPopupPositionY}
                selectedStatus={this.state.actionPopupForConsultationRequest.Status}
                selectStatusOptions={MedicalRequestStatus.getComboboxItems()}
                showStatusControls
                onCancel={() => this.mutateState(s => {
                    s.actionPopupIsOpen = false;
                    $("table.myle-table tbody tr").removeClass("active");
                })}
                onSave={async (note: string, messageId: string, status?: string) => {
                    await Client.postAsync(
                        ServerOperations.Server.Myle_ConsultationRequest_EditCommentAndStatus,
                        {
                            ConsultationRequestId: messageId,
                            StatusLookupId: status,
                            Comment: note
                        }
                    );

                    this.closeActionPopup();
                    sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_Reload);
                }}
                footerClassName={this.footerClassName}
                dataCy="consultationRequest-notePopup"
            />
        );
    }

    private getUrgentClassName(item: MyConsultationRequestsTableItem): string {
        return this.isUrgentPriority(item.Model.PriorityDropdownId) || moment() > moment(item.Model.DueDate)
            ? "red-text"
            : "";
    }

    private isUrgentPriority(priorityId: string): boolean {
        return priorityId === ConsultationRequestPriorityOption.Urgent.model.Id;
    }

    private getPriorityString(priorityId: string): string {
        const allPriorities = ConsultationRequestPriorityOption.getAll();
        return allPriorities.firstOrDefault(p => p.model.Id === priorityId)?.getName() ?? "";
    }

    private getStatusString(status: string): string {
        const statusesLookUps = cacheStore.FindLookupsByTypeName("MedicalRequestStatus");
        return statusesLookUps.firstOrDefault(p => p.LookupID === status)?.GetValue() ?? "";
    }

    private getCreatedByString(createdById: string): string {
        const employee = this.props.Model.get("CreatedByGlobalCache")
            ?.toArray()
            .firstOrDefault(p => p.ContactId === createdById);

        return employee?.Name ?? "";
    }

    private canSendToPortal(item: ConsultationRequestsInboxItem): boolean {
        const syncState = item.PatientPortalSyncState;
        const portalEnabled = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

        // check if consult request is signed and the patient registration status is applicable
        return (
            portalEnabled
            && item.SignedPrintAuthorizations?.any()
            && (
                syncState == PortalRegistrationState.RegistrationComplete
                || syncState == PortalRegistrationState.RegistrationPending
                || syncState == PortalRegistrationState.ChildAccount
            )
        );
    }

    private onPortalClick(item: ConsultationRequestsInboxItem): void {
        const showWarningToSyncToPortal = PortalHelper.showWarningToSyncToPortal(
            item.SharedItem,
            item.RequestingStaffContactFacetId,
            item.CreatedByContactId,
            SignedPrintAuthorization.getLast(item.SignedPrintAuthorizations)
        );

        if (showWarningToSyncToPortal) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: cacheStore.GetString("WarningSignRequestingStaffPortalLabel")
            });
        }
        else {
            sessionStore.dispatch(LocalOperations.Portfolio.MyConsultationRequests_SendToPortal_IconClick, {
                portalShareableItem: item,
                patientContactId: item.ContactId,
                patientPortalSyncState: item.PatientPortalSyncState
            });
        }
    }

    private getNextAppointmentText(item: MyConsultationRequestsTableItem): string {
        if (!item?.Model?.PatientNextAppointmentDateTime)
            return;

        // today
        if (DateHelper.formatIsoDateOnly(item.Model.PatientNextAppointmentDateTime) === DateHelper.formatIsoDateOnly(new Date()))
            return `${cacheStore.GetString("TodayLabel")} ${LuxonDateTimeParser.parse(item.Model.PatientNextAppointmentDateTime).toFormat("HH:mm")}`;

        // upcoming date
        const dateStr = LuxonDateTimeParser.parse(item.Model.PatientNextAppointmentDateTime).toFormat("yyyy-MM-dd HH:mm");
        return item.Model.HasAppointmentToday
            ? `${cacheStore.GetString("TodayAbbrevationLabel")}+ ${dateStr}`
            : dateStr;
    }

    private getFormattedConsultationDateTime(consultationDate: Date): string | null {
        if (!consultationDate)
            return;

        const luxonDate = LuxonDateTimeParser.parse(consultationDate);

        if (luxonDate.hour === 0 && luxonDate.minute === 0)
            return DateHelper.formatDateLuxon(consultationDate, "yyyy-MM-dd");

        return LuxonDateTimeParser.parse(consultationDate).toFormat("yyyy-MM-dd HH:mm");
    }
}

export { ConsultationRequestInbox };
